<template>
  <TextField
    :id="id"
    ref="input"
    v-model="content"
    :labelName="labelName"
    :name="name"
    :type="'text'"
    :status="status"
    :placeholder="placeholder"
    :disabled="disabled"
    :required="required"
    :readonly="readonly"
    :maxlength="maxlength"
    :helperText="displayHelperText"
    :iconStatic="iconStatic"
    refName="input"
    @blur="$emit('blur')"
  />
</template>

<script lang="ts">
import { subscribeGoogleMapsReady } from '@/lib/googleMaps';
import helperStatus from '../../helpers/status';
import TextField from '../textfield/TextField.vue';

export default {
  name: 'GooglePlacesTextField',
  components: {
    TextField,
  },
  model: {
    prop: 'modelValue',
    event: 'update:modelValue',
  },
  props: {
    labelName: {
      type: String,
      required: true,
      validator(value: String) {
        return !!value;
      },
    },
    id: {
      type: String,
      required: true,
      validator(value: String) {
        return !!value;
      },
    },
    name: {
      type: String,
      default: '',
      required: false,
    },
    status: {
      type: Number as () => helperStatus,
      default: helperStatus.Neutral,
    },
    modelValue: {
      type: String,
      default: '',
      required: false,
    },
    placeholder: {
      type: String,
      default: '',
      required: false,
    },
    disabled: {
      type: Boolean,
      required: false,
    },
    required: {
      type: Boolean,
      required: false,
    },
    readonly: {
      type: Boolean,
      required: false,
    },
    maxlength: {
      type: String,
      default: undefined,
      required: false,
    },
    helperText: {
      type: String,
      default: '',
      required: false,
    },
    iconStatic: {
      type: Boolean,
      default: false,
      required: false,
    },
  },
  data(): {
    content: string;
    mapsLoadFailed: boolean;
    placesAutocomplete: any | null;
    isActive: boolean;
    unsubscribeGoogleMaps: (() => void) | null;
  } {
    return {
      content: this.modelValue,
      mapsLoadFailed: false,
      placesAutocomplete: null,
      isActive: true,
      unsubscribeGoogleMaps: null,
    };
  },
  computed: {
    displayHelperText(): string {
      if (this.helperText) {
        return this.helperText;
      }

      if (this.mapsLoadFailed) {
        return 'Address suggestions are unavailable. You can still type your address.';
      }

      return '';
    },
  },
  watch: {
    content(selectedValue: String) {
      this.$emit('update:modelValue', selectedValue);
    },
    modelValue(valueFromParent: String) {
      this.content = valueFromParent;
    },
  },
  mounted() {
    this.unsubscribeGoogleMaps = subscribeGoogleMapsReady((ready) => {
      this.handleGoogleMapsReady(ready);
    });
  },
  beforeUnmount() {
    this.isActive = false;
    this.unsubscribeGoogleMaps?.();
    this.unsubscribeGoogleMaps = null;

    if (this.placesAutocomplete && window.google?.maps?.event) {
      window.google.maps.event.clearInstanceListeners(this.placesAutocomplete);
      this.placesAutocomplete = null;
    }
  },
  methods: {
    handleGoogleMapsReady(ready: boolean) {
      if (!this.isActive) {
        return;
      }

      this.mapsLoadFailed = !ready;

      if (!ready) {
        return;
      }

      void this.$nextTick(() => {
        if (this.isActive) {
          this.attachPlacesAutocomplete();
        }
      });
    },
    attachPlacesAutocomplete() {
      if (!this.isActive) {
        return;
      }

      const textField = this.$refs.input as InstanceType<typeof TextField> | undefined;
      const inputEl = textField?.$refs?.input as HTMLInputElement | undefined;

      if (!inputEl || typeof window.google?.maps?.places?.Autocomplete !== 'function') {
        this.mapsLoadFailed = true;
        return;
      }

      this.placesAutocomplete = new window.google.maps.places.Autocomplete(inputEl);
      this.placesAutocomplete.addListener('place_changed', () => {
        this.$emit('autoCompletedAddresses', this.placesAutocomplete?.getPlace());
      });
    },
  },
};
</script>
